import React, { useState, useMemo } from 'react';
import { Row, Col, Input, Table, Badge } from 'reactstrap';
import { ButtonBuilder, ToastBuilder, ApiRequest, CommonModal, ModalButtonBuilder } from '../../../../../../../../../GlobalComponents/Builders';

/**
 * RecordOverrideImportModal — bulk-create destination overrides from a pasted
 * two-column list (copied from Excel/Sheets, tab-delimited).
 *
 * Fixed format, one override per line:
 *
 *   Source Record ID  <tab>  New Destination ID
 *   3489                      1077
 *
 * The whole batch shares one source resource (the child/site resource) and one
 * override resource (the company resource), set once above the paste box. So the
 * line above re-points: resource=sites, source_rec_id=3489 -> override company 1077.
 *
 * Migration jobs only.
 */

const RecordOverrideImportModal = ({ jobId, trigger, onImportComplete, defaultResourceId = '', defaultOverrideResourceId = '', resourceOptions = [] }) => {

    const [modal, setModal] = useState(false);

    // Resource ids shared by the whole batch. Prefilled from the resource-mapping
    // row when opened there (the child/source resource), but still editable.
    const [resourceId, setResourceId] = useState(String(defaultResourceId || ''));          // child/site resource (e.g. 109)
    const [overrideResourceId, setOverrideResourceId] = useState(String(defaultOverrideResourceId || '')); // company resource (e.g. 97)

    const [rawData, setRawData] = useState('');
    const [importing, setImporting] = useState(false);

    // Source resource is seeded from the resource-mapping row we opened from — show
    // it read-only. Fall back to the raw id if it isn't in the options list.
    const sourceResourceLabel = useMemo(() => {
        const match = resourceOptions.find(opt => String(opt.value) === String(resourceId));
        return match ? `${match.label} (${match.value})` : (resourceId || '—');
    }, [resourceOptions, resourceId]);

    // Fixed two-column parse: col 0 = source record id, col 1 = new destination id.
    const rows = useMemo(() => {
        if (!rawData.trim()) return [];
        return rawData.trim().split('\n').map(line => {
            const parts = line.split('\t');
            return {
                sourceRecId: (parts[0] || '').trim(),
                overrideDestinationRecId: (parts[1] || '').trim(),
            };
        });
    }, [rawData]);

    const validRows = useMemo(
        () => rows.filter(r => /^\d+$/.test(r.sourceRecId) && /^\d+$/.test(r.overrideDestinationRecId)),
        [rows]
    );

    const handleImport = async () => {
        if (!resourceId || !overrideResourceId) {
            return ToastBuilder('error', 'Source Resource ID and Override Resource ID are required');
        }
        if (validRows.length === 0) {
            return ToastBuilder('error', 'No valid rows — each line must be: source record ID <tab> new destination ID');
        }

        const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/overrides/bulk', {
            pathParams: { jobId },
            body: {
                resourceId: parseInt(resourceId),
                overrideResourceId: parseInt(overrideResourceId),
                rows: validRows.map(r => ({
                    sourceRecId: parseInt(r.sourceRecId),
                    overrideDestinationRecId: parseInt(r.overrideDestinationRecId),
                })),
            },
            setIsProcessing: setImporting,
            toastOnError: true,
        });

        const data = res.data || {};
        if (res.success && data.success) {
            ToastBuilder(data.failed ? 'error' : 'success', data.message || 'Import complete');
            if (data.errors && data.errors.length) {
                // Surface the first few skipped rows so the operator can fix them.
                data.errors.slice(0, 5).forEach(e => ToastBuilder('error', `Record ${e.sourceRecId}: ${e.message}`));
            }
            onImportComplete?.();
            if (!data.failed) {
                setRawData('');
                setModal(false);
            }
        } else {
            ToastBuilder('error', data.message || 'Import failed');
        }
    };

    const previewRows = useMemo(() => rows.slice(0, 6), [rows]);

    const body = (
        <div>
            <p className='text-muted' style={{ fontSize: 12 }}>
                Re-point migrated records to a promoted destination entity (e.g. sites that became standalone
                companies). Set the source and override resource IDs once, then paste one override per line.
            </p>

            <Row className='mb-3'>
                <Col md={6}>
                    <label style={{ fontSize: 11, color: '#888' }}>Source Resource (child, e.g. sites)</label>
                    <Input bsSize='sm' type='text' value={sourceResourceLabel} disabled readOnly />
                </Col>
                <Col md={6}>
                    <label style={{ fontSize: 11, color: '#888' }}>Override Resource (e.g. companies)</label>
                    <Input bsSize='sm' type='select' value={overrideResourceId} onChange={(e) => setOverrideResourceId(e.target.value)}>
                        <option value=''>Select resource…</option>
                        {resourceOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label} ({opt.value})</option>
                        ))}
                    </Input>
                </Col>
            </Row>

            <p style={{ fontSize: 12, marginBottom: 2 }}>Paste tab-separated overrides, one per line:</p>
            <p style={{ fontSize: 12, marginBottom: 6 }}><b>Source Record ID → New Destination ID</b></p>
            <Input
                type='textarea'
                value={rawData}
                onChange={(e) => setRawData(e.target.value)}
                placeholder={'3489\t1077\n3601\t1076\n1642\t1078'}
                style={{ height: 200, fontFamily: 'monospace', fontSize: 12 }}
            />

            {previewRows.length > 0 && (
                <div className='mt-3'>
                    <span style={{ fontSize: 11, fontWeight: 600 }}>
                        Preview — <Badge color='success'>{validRows.length} valid</Badge> of {rows.length} line(s)
                    </span>
                    <div style={{ maxHeight: 160, overflow: 'auto', border: '1px solid #eee', borderRadius: 4, padding: 4, marginTop: 4 }}>
                        <Table size='sm' style={{ fontSize: 11, marginBottom: 0 }}>
                            <thead>
                                <tr>
                                    <th>Source Record ID</th>
                                    <th>→ New Destination ID</th>
                                </tr>
                            </thead>
                            <tbody>
                                {previewRows.map((r, i) => {
                                    const ok = /^\d+$/.test(r.sourceRecId) && /^\d+$/.test(r.overrideDestinationRecId);
                                    return (
                                        <tr key={i} className={ok ? '' : 'text-danger'}>
                                            <td>{r.sourceRecId || '—'}</td>
                                            <td>{r.overrideDestinationRecId || '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                </div>
            )}

            <div className='text-end mt-3'>
                <ButtonBuilder
                    label={importing ? 'Importing...' : `Import ${validRows.length || ''} Override(s)`}
                    className='btn-success btn-sm px-3'
                    disabled={importing || validRows.length === 0}
                    onClick={handleImport}
                    useSpinner={importing}
                />
            </div>
        </div>
    );

    // Default rendering matches the sibling dropdown actions (e.g. "Import Records"):
    // a ModalButtonBuilder styled button. A custom `trigger` can override it.
    if (!trigger) {
        return (
            <ModalButtonBuilder
                title='Import Overrides'
                className='btn-outline-info'
                modalTitle='Bulk Import Destination Overrides'
                modalSize='lg'
                onSubmit={false}
                onCancelLabel='Close'
                body={body}
            />
        );
    }

    return (
        <>
            <span onClick={() => setModal(true)} style={{ display: 'inline-flex' }}>{trigger}</span>
            <CommonModal isOpen={modal} title='Bulk Import Destination Overrides' toggler={() => setModal(false)} size='lg' onSubmit={false} onCancelLabel='Close'>
                {body}
            </CommonModal>
        </>
    );
};

export default RecordOverrideImportModal;
